import React, { useEffect, useState } from "react";
import { StyleSheet } from "react-native";
import type { IconProps } from "~/components/Icon";
import { Icon } from "~/components/Icon";
import { styleConstants as C } from "~/style/styleConstants";
import { isPromise } from "~/utils/isPromise";
import { Modal } from "./Modal";
import { Pressable } from "./Pressable";
import { Spinner } from "./Spinner";
import { View } from "./View";
import type { PressableProps } from "./Pressable";

export interface IconButtonProps extends PressableProps {
  iconName: IconProps["name"];
  iconSize?: IconProps["size"];
  iconColor?: IconProps["color"];
  iconStyle?: IconProps["style"];
  isLoading?: boolean;
}

const HIT_SLOP: PressableProps["hitSlop"] = {
  top: 8,
  right: 8,
  bottom: 8,
  left: 8,
};

export const IconButton = React.forwardRef(function IconButton(
  {
    iconName,
    iconSize,
    iconColor,
    iconStyle,
    onPress,
    isLoading: isLoadingProp,
    style,
    ...props
  }: IconButtonProps,
  ref: any,
) {
  const isMounted = React.useRef<boolean>(true);
  useEffect(() => {
    return () => {
      isMounted.current = false;
    };
  }, []);

  const [isLoadingState, setIsLoading] = useState(false);
  const isLoading = isLoadingProp || isLoadingState;

  return (
    <Pressable
      ref={ref}
      disabled={isLoading || props.disabled}
      hitSlop={HIT_SLOP}
      onPress={(event) => {
        if (typeof onPress === "function") {
          const maybePromise = onPress(event);

          if (isPromise(maybePromise)) {
            setIsLoading(true);

            maybePromise.finally(() => {
              if (isMounted.current) {
                setIsLoading(false);
              }
            });
          }
        }
      }}
      style={({ pressed, hovered }) => [
        { opacity: pressed ? 0.2 : 1 },
        typeof style === "function" ? style({ pressed, hovered }) : style,
      ]}
      {...props}
    >
      <Icon
        name={iconName}
        size={iconSize}
        color={iconColor}
        style={iconStyle}
      />

      {isLoading && (
        <View centerContent style={StyleSheet.absoluteFill}>
          <Spinner size="small" color={C.colorTextDarkSoft} />
        </View>
      )}

      {isLoading && <Modal blockHardwareBackButton />}
    </Pressable>
  );
});
